import { useEffect, useRef, useState } from "react";
import { useTranslate } from "helpers";
import { Errors } from "services/forms/validation/types";
import { NotificationDefinitionFormValues, NotificationScope } from "services/api/notifications/types";
import { initialNotificationDefinitionForm } from "services/api/notifications/consts";
import {
  createNotificationDefinitionAPI,
  getNotificationDefinitionAPI,
  updateNotificationDefinitionAPI,
} from "services/api/notifications/api";
import { transformDefinitionToForm } from "services/api/notifications/transformations";
import WizardStep from "ui/components/Wizard/WizardStep";
import { Wizard } from "ui/components/Wizard";
import WizardFooter from "ui/components/Wizard/WizardFooter";
import { Loader } from "ui/components/Loader";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import Step1BasicInfo from "./Step1BasicInfo";
import StepMessageSetup from "./StepMessageSetup";
import Step2EntitySelection from "./Step2EntitySelection";
import Step3Recipients from "./Step3Recipients";
import Step4Rules from "./Step4Rules";
import Step5Summary from "./Step5Summary";
import NotificationIntro from "./components/NotificationIntro/NotificationIntro";
import { NotificationIntroScene } from "./components/NotificationIntro/types";
import { NotificationEditProps } from "./types";


const NotificationEditCard: React.FC<NotificationEditProps> = (props) => {
  const { show, definitionId, onSuccess, onClose } = props;

  const { t } = useTranslate();
  const isEdit = !!definitionId;

  const [stepCurrent, setStepCurrent] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValues] = useState<NotificationDefinitionFormValues>(
    initialNotificationDefinitionForm
  );
  const [errors, setErrors] = useState<Errors>({});

  const stepValidators = useRef<Record<number, () => boolean | Promise<boolean>>>({});

  // Ovisi i o `show`: kod unosa nove notifikacije `definitionId` je uvijek null, pa se sam
  // po sebi nikad ne promijeni. Komponenta ostaje montirana i nakon zatvaranja modala, pa bi
  // se sljedeće otvaranje vratilo na zadnji korak prethodnog unosa.
  useEffect(() => {
    if (!show) return;

    setFormValues(initialNotificationDefinitionForm);
    setStepCurrent(0);
    setErrors({});
    // Validatori se ključaju po indeksu koraka, a indeksi ovise o opsegu i prilagođenom
    // tipu — stari bi se mogli okinuti na koraku koji im ne pripada.
    stepValidators.current = {};

    if (definitionId) {
      fetchDefinition(definitionId);
    }
  }, [show, definitionId]);

  const fetchDefinition = async (defId: string) => {
    setIsLoading(true);
    try {
      const result = await getNotificationDefinitionAPI(defId);
      const formVals = transformDefinitionToForm(result);
      setFormValues(formVals);
    } catch {
      // error handled by createApiCall
    }
    setIsLoading(false);
    setErrors({});
  };

  const handleNextStep = async () => {
    const stepValidator = stepValidators.current[stepCurrent];
    if (stepValidator) {
      const isValid = await stepValidator();
      if (!isValid) return;
    }
    setStepCurrent((s) => s + 1);
  };

  const handleBackStep = () => {
    setStepCurrent((s) => s - 1);
  };

  const handleFinish = async () => {
    const stepValidator = stepValidators.current[stepCurrent];
    if (stepValidator) {
      const isValid = await stepValidator();
      if (!isValid) return;
    }

    setIsLoading(true);
    try {
      if (isEdit) {
        await updateNotificationDefinitionAPI(definitionId!, formValues, t);
      } else {
        await createNotificationDefinitionAPI(formValues, t);
      }
      if (onSuccess) onSuccess();
    } catch {
      // error handled by createApiCall
    }
    setIsLoading(false);
  };

  const registerStepValidator = (
    step: number,
    validator: () => boolean | Promise<boolean>
  ) => {
    stepValidators.current[step] = validator;
  };

  const isIndividual = Number(formValues.scope) === NotificationScope.Individual && !formValues.isCustom;

  // Build dynamic step indices
  // 0 = odabir grupe, 1 = postavke obavijesti; ostalo ovisi o opsegu i prilagođenom tipu.
  const messageSetupStepIndex = 1;
  let stepIdx = 2;
  const entityStepIndex = isIndividual ? stepIdx++ : -1;
  const recipientsStepIndex = stepIdx++;
  const rulesStepIndex = !formValues.isCustom ? stepIdx++ : -1;
  const summaryStepIndex = stepIdx;

  // Scena lijevog panela po koraku; prati isti raspored koraka kao `steps` niže.
  const introScenes: NotificationIntroScene[] = [
    "type",
    "message",
    ...(isIndividual ? ["entities" as const] : []),
    "recipients",
    ...(!formValues.isCustom ? ["rules" as const] : []),
    "summary",
  ];

  const steps = [
    <WizardStep key={1} stepNumber={0} stepCurrent={stepCurrent}>
      <Step1BasicInfo
        formValues={formValues}
        setFormValues={setFormValues}
        errors={errors}
        setErrors={setErrors}
        registerValidator={(fn) => registerStepValidator(0, fn)}
        isEdit={isEdit}
      />
    </WizardStep>,
    <WizardStep key="messageSetup" stepNumber={messageSetupStepIndex} stepCurrent={stepCurrent}>
      <StepMessageSetup
        formValues={formValues}
        setFormValues={setFormValues}
        errors={errors}
        setErrors={setErrors}
        registerValidator={(fn) => registerStepValidator(messageSetupStepIndex, fn)}
        isEdit={isEdit}
      />
    </WizardStep>,
    ...(isIndividual
      ? [
          <WizardStep key={2} stepNumber={entityStepIndex} stepCurrent={stepCurrent}>
            <Step2EntitySelection
              formValues={formValues}
              setFormValues={setFormValues}
              errors={errors}
              setErrors={setErrors}
              registerValidator={(fn) => registerStepValidator(entityStepIndex, fn)}
            />
          </WizardStep>,
        ]
      : []),
    <WizardStep key={3} stepNumber={recipientsStepIndex} stepCurrent={stepCurrent}>
      <Step3Recipients
        formValues={formValues}
        setFormValues={setFormValues}
        errors={errors}
        setErrors={setErrors}
        registerValidator={(fn) => registerStepValidator(recipientsStepIndex, fn)}
      />
    </WizardStep>,
    ...(!formValues.isCustom
      ? [
          <WizardStep key={4} stepNumber={rulesStepIndex} stepCurrent={stepCurrent}>
            <Step4Rules
              formValues={formValues}
              setFormValues={setFormValues}
              errors={errors}
              setErrors={setErrors}
              registerValidator={(fn) => registerStepValidator(rulesStepIndex, fn)}
            />
          </WizardStep>,
        ]
      : []),
    <WizardStep key={5} stepNumber={summaryStepIndex} stepCurrent={stepCurrent}>
      <Step5Summary
        formValues={formValues}
        setFormValues={setFormValues}
        errors={errors}
        setErrors={setErrors}
      />
    </WizardStep>,
  ];

  return (
    <Modal
      show={show}
      onHide={onClose}
      position="side"
      size="full"
      // Intro ide kroz Modal, ne kroz Wizard — samo ga Modal slaže lijevo uz formu.
      // Kroz `stepCurrentSummary` bi se crtao kao blok iznad koraka.
      intro={{
        content: (
          <NotificationIntro
            formValues={formValues}
            scene={introScenes[stepCurrent] ?? "type"}
            isEdit={isEdit}
            stepCurrent={stepCurrent}
            stepCount={steps.length}
          />
        ),
      }}
    >
      <ModalHeader onHide={onClose} />
      <ModalBody>
        <Wizard steps={steps} />
        <Loader isLoading={isLoading}> </Loader>
      </ModalBody>
      <WizardFooter
        stepCurrent={stepCurrent}
        steps={steps}
        onNextStep={handleNextStep}
        onBackStep={handleBackStep}
        onFinish={handleFinish}
        isLoading={isLoading}
        isReadOnly={false}
      />
    </Modal>
  );
};

export default NotificationEditCard;
